// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import { MerchantProgramme } from 'src/@types/merchant';
import { firstLetterUpperCase, lowerCase } from 'src/utils/stringFormatter';
import { NavListProps } from 'src/components/nav-section';
import { FileNavIcon, ProfileNavIcon, GlobNavIcon, CheckIcon, PaymentIcon, SettingsIcon, CO2Icon, CreditsIcon, TextIcon, CustomIcon, GlobeIcon } from 'src/theme/overrides/CustomIcons';

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  dashboard: getIcon('ic_dashboard'),
  toolkit: getIcon('ic_toolkit'),
  impactTools: getIcon('ic_impact_tools'),
  credits: getIcon('ic_credits'),
  myCredits: getIcon('ic_my_credits'),
  businessCalculator: getIcon('ic_business_calculator'),
  calculators: getIcon('ic_calculators'),
  impactPages: getIcon('ic_impact_pages'),
  impactStories: getIcon('ic_impact_stories'),
  settings: getIcon('ic_menu_settings'),
  emissions: getIcon('ic_co2'),
  programs: getIcon('ic_people'),
  account: getIcon('ic_settings'),
  impact: getIcon('ic_settings'),
  event: getIcon('ic_event'),
};

const generateRouteChildren = (el: any) => {
  const repeatableEachProgrammeLinks = [
    {
      title: 'Transactions',
      path: PATH_DASHBOARD.programs.transactions(lowerCase(el.type), el.code),
      id: 'nav-item-collective-credits',
      icon: <FileNavIcon />
    },
    {
      title: 'Engagement',
      path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code),
      id: 'nav-item-engagement',
      icon: <CheckIcon />
    },
    {
      title: 'Impact',
      path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code),
      id: 'nav-item-impact',
      icon: <GlobNavIcon />
    },
  ];

  const children: { [key: string]: NavListProps[] } = {
    FLIGHT: [
      // repeatableEachProgrammeLinks - sharable items across every menu programme
      ...repeatableEachProgrammeLinks,
      {
        title: 'Customers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    JOURNEY: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Customers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    EMPLOYEE: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Employees',
        path: PATH_DASHBOARD.programs.employees(lowerCase(el.type), el.code),
        id: 'nav-item-employees',
        icon: <ProfileNavIcon />
      },
    ],
  }

  return children[el.type];
}

export const getProgramMenu = (programmes: MerchantProgramme[]) => {
  const menu = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const title = `${el.type === 'FLIGHT' ? `${firstLetterUpperCase(el.type)} offsetting` : `${firstLetterUpperCase(el.type)} programme`}`;
    return {
      title,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      id: 'nav-item-merchant-programme',
      icon: ICONS.credits,
      programme: el.type,
      children: children,
    }
  })

  return menu;
};

export const contributionsMenuItem = [
  {
    title: 'Contributions',
    path: PATH_DASHBOARD.programs.collectiveCredits,
    id: 'nav-item-collective-credits',
    icon: ICONS.credits
  }
];

export const TLSettingsMenuItems = [
  {
    title: 'Page settings',
    path: '#generalSettings', // equals to section id
    icon: <SettingsIcon />,
  },
  {
    title: 'Header',
    path: '#headerSection',
    icon: <TextIcon />,
  },
  {
    title: 'Intro',
    path: '#introSection',
    icon: <GlobeIcon />,
  },
  {
    title: 'Impact',
    path: '#impactSection',
    icon: <CO2Icon />,
  },
  {
    title: 'Explainer',
    path: '#howItWorksSection',
    icon: <CreditsIcon />,
  },
  {
    title: 'Custom',
    path: '#customSection',
    icon: <CustomIcon />,
  },
];

export const merchantStatsMenu = [
  {
    title: 'Insights',
    path: PATH_DASHBOARD.impactStories.insights.root,
    id: 'nav-item-insights',
    icon: ICONS.dashboard,
    children: [
      {
        title: 'Impact',
        path: PATH_DASHBOARD.impactStories.insights.impact,
        id: 'nav-item-insights-impact',
        icon: <GlobNavIcon />
      },
      {
        title: 'Projects',
        path: PATH_DASHBOARD.impactStories.insights.projects,
        id: 'nav-item-insights-projects',
        icon: <PaymentIcon sx={{ '& > *': { fill: '#74635B' } }} />
      },
      {
        title: 'Transactions',
        path: PATH_DASHBOARD.impactStories.insights.transactions,
        id: 'nav-item-insights-transactions',
        icon: <FileNavIcon />
      },
    ],
  },
];

const navConfig = [

  {
    items: [
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        id: 'nav-item-dashboard',
      },
    ],
  },
  {
    items: [
      {
        title: 'My credits',
        path: PATH_DASHBOARD.credits.purchaseHistory,
        id: 'nav-item-view-credits',
        icon: ICONS.myCredits,
      },
    ],
  },

  {
    items: merchantStatsMenu,
  },


  {
    title: 'PROGRAMMES',
    id: 'nav-item-group-programmes',
    items: [
      {
        title: 'Toolkit',
        path: PATH_DASHBOARD.programs.toolkit,
        id: 'nav-item-toolkit',
        icon: ICONS.toolkit
      },
    ],
  },

  {
    title: 'EMISSIONS',
    id: 'nav-item-group-emissions',
    items: [
      {
        title: 'Emission records',
        path: PATH_DASHBOARD.emissions.emissionRecords,
        id: 'nav-item-business-calculator',
        icon: ICONS.emissions,
      },
      {
        title: 'Business calculator',
        path: PATH_DASHBOARD.emissions.businessCalculator,
        id: 'nav-item-business-calculator',
        icon: ICONS.businessCalculator,
      },
      {
        title: 'Event calculator',
        path: PATH_DASHBOARD.emissions.eventCalculator,
        id: 'nav-item-event-calculator',
        icon: ICONS.event,
      },
    ],
  },

  {
    title: 'IMPACT STORIES',
    id: 'nav-item-group-impact-stories',
    items: [
      {
        title: 'Landing pages',
        path: PATH_DASHBOARD.impactStories.impactPages,
        id: 'nav-item-impact-pages',
        icon: ICONS.impactPages
      },
      {
        title: 'Impact tools',
        path: PATH_DASHBOARD.impactStories.tools,
        id: 'nav-item-impact-stories-toolkit',
        icon: ICONS.impactTools
      },
    ],
  },

  {
    title: 'CONFIGURATION',
    id: 'nav-item-group-configuration',
    items: [
      {
        title: 'Settings',
        path: PATH_DASHBOARD.settings.root,
        id: 'nav-item-settings',
        icon: ICONS.settings,
        children: [
          {
            title: 'Theme',
            path: PATH_DASHBOARD.settings.theme,
            id: 'nav-item-theme'
          },
        ],
      }
    ]
  },
];

export default navConfig;
